import { createElement, useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import SerpContentSnapshot from '@/components/Approval/@components/SerpContentSnapshot';
import type { ElementTransform } from '@/components/Common/HtmlToReact';
import {
  LEGACY_CREDIT_NOTE_OZ,
  openCreditNoteOzReport,
  type CreditNoteOzPrint,
} from '@/utils/approval/creditNote';
import { ACTION_CELL_CLASS, CARD_CLASS } from './content';
import { CreditNoteRoot } from './styles';

export { buildCreditNoteHtml } from './content';

/** 레거시 문서 카드의 버튼 */
const LEGACY_PRINT_CLASS = 'btn-printsc';
const LEGACY_DELETE_CLASS = 'btn-detail-hide';
const LEGACY_REJECT_CLASS = 'btn-detail-reject';

export type CreditNoteTarget = { itemId: string; label: string };
export type CreditNoteCardRef = CreditNoteTarget & { aprvRev: string };

/** 부분반려 버튼을 붙일 카드 수 — V2 카드 + 레거시 부분반려 버튼 */
export const countCreditNoteCards = (content: string | null) =>
  content
    ? (content.match(new RegExp(`${CARD_CLASS}"|btn-detail-reject`, 'g')) ?? [])
        .length
    : 0;

/**
 * 저장 카드 HTML 그대로(레거시 문서 포함). DETAIL 은 작성·조회 모두,
 * 삭제는 작성 화면에서만, 카드별 버튼(부분반려)은 조회 화면에서만
 */
export const CreditNoteSnapshot = ({
  content,
  onDelete,
  renderCardAction,
}: {
  content: string;
  onDelete?: (target: CreditNoteTarget) => void;
  renderCardAction?: (card: CreditNoteCardRef) => ReactNode;
}) => {
  const { t } = useTranslation();

  const transform = useCallback<ElementTransform>(
    ({ element, tagName, props }) => {
      const { classList } = element;
      const detailButton = (oz: CreditNoteOzPrint) => (
        <BoxButton
          key="detail"
          type="button"
          variant="secondary"
          size="small"
          onClick={() => openCreditNoteOzReport(oz)}
        >
          DETAIL
        </BoxButton>
      );
      if (classList.contains(ACTION_CELL_CLASS)) {
        const oz: CreditNoteOzPrint = {
          url: element.getAttribute('data-oz-url'),
          gerpOzrNm: element.getAttribute('data-oz-ozr'),
          gerpOdiNm: element.getAttribute('data-oz-odi'),
          pcount: element.getAttribute('data-oz-pcount'),
          args1: element.getAttribute('data-oz-args1'),
          locale: element.getAttribute('data-oz-locale'),
        };
        const card = element.closest(`.${CARD_CLASS}`);
        const target = {
          itemId: card?.getAttribute('data-item-id') ?? '',
          label: card?.getAttribute('data-label') ?? '',
        };
        const action = target.itemId
          ? renderCardAction?.({
              ...target,
              aprvRev: card?.getAttribute('data-aprv-rev') ?? '',
            })
          : null;
        return createElement(
          tagName,
          props,
          oz.args1 ? detailButton(oz) : null,
          onDelete && target.itemId ? (
            <BoxButton
              key="delete"
              type="button"
              variant="secondary"
              size="small"
              onClick={() => onDelete(target)}
            >
              {t('삭제')}
            </BoxButton>
          ) : null,
          action ?? null,
        );
      }
      if (classList.contains(LEGACY_PRINT_CLASS)) {
        const creditNo = element.getAttribute('data-credit_no') ?? '';
        return creditNo
          ? detailButton({
              ...LEGACY_CREDIT_NOTE_OZ,
              args1: `credit_no=${creditNo}`,
            })
          : null;
      }
      if (classList.contains(LEGACY_REJECT_CLASS)) {
        const itemId = element.getAttribute('data-aprv_id') ?? '';
        return itemId
          ? renderCardAction?.({
              itemId,
              label: element.getAttribute('data-credit_no') ?? '',
              aprvRev: element.getAttribute('data-aprv_rev') ?? '',
            }) ?? null
          : null;
      }
      if (classList.contains(LEGACY_DELETE_CLASS)) return null;
      return undefined;
    },
    [onDelete, renderCardAction, t],
  );

  return (
    <CreditNoteRoot>
      <SerpContentSnapshot content={content} transform={transform} />
    </CreditNoteRoot>
  );
};
